import CustomButton from 'components/Buttons/CustomButton'
import PMBInputNumber from 'components/PMBInputNumber'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { Lock, Minus, Plus, Trash, Unlock } from 'react-feather'
import { useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'

import { truncateTwoDecimals } from 'helpers'
import {
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap'
import { parseFormattedNumber } from 'utility/Utils'
import { newFormatThousandSeparator } from 'utils/formatNumber'
import { getInventories, splitInventory } from '../sellCommodityAction'

const ModalSplit = ({ isOpen, toggle, selectedItem, onSplitSuccess }) => {
  const dispatch = useDispatch()
  const intl = useIntl()
  const { enqueueSnackbar } = useSnackbar()
  const [splits, setSplits] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSyncingSplitResult, setIsSyncingSplitResult] = useState(false)
  const [lockedIndexes, setLockedIndexes] = useState(new Set())
  const [addCount, setAddCount] = useState(1)

  useEffect(() => {
    if (isOpen && selectedItem) {
      const originalAmount =
        selectedItem?.originalAmount || selectedItem?.amount || 0

      const p = 2
      const base = Math.floor(originalAmount / p)
      const remainder = originalAmount % p

      const getAmount = (idx) => {
        if (idx < Math.floor(remainder)) return base + 1
        if (idx === Math.floor(remainder) && remainder % 1 > 0)
          return base + (remainder % 1)
        return base
      }

      setSplits([
        {
          amount: getAmount(0),
          unit: selectedItem?.unit?.value || selectedItem?.unit || 'Kg',
        },
        {
          amount: getAmount(1),
          unit: selectedItem?.unit?.value || selectedItem?.unit || 'Kg',
        },
      ])

      setLockedIndexes(new Set())
      setAddCount(1)
      setIsSyncingSplitResult(false)
    }
  }, [isOpen, selectedItem])

  const handleAddSplit = (count = 1) => {
    const originalAmount =
      selectedItem?.originalAmount || selectedItem?.amount || 0

    const newSplits = [...splits]
    for (let i = 0; i < count; i++) {
      newSplits.push({
        amount: 0,
        unit: selectedItem?.unit?.value || selectedItem?.unit || 'Kg',
      })
    }

    let totalLockedAmount = 0
    newSplits.forEach((split, idx) => {
      if (lockedIndexes.has(idx)) {
        const amt =
          typeof split.amount === 'string'
            ? Number(split.amount?.replace(/\./g, '')?.replace(/\,/g, '.'))
            : split.amount
        totalLockedAmount += isNaN(amt) ? 0 : amt
      }
    })

    const remainingAmount = originalAmount - totalLockedAmount

    const unlockedIndexes = []
    newSplits.forEach((_, idx) => {
      if (!lockedIndexes.has(idx)) {
        unlockedIndexes.push(idx)
      }
    })

    if (unlockedIndexes.length > 0) {
      const p = unlockedIndexes.length
      const base = Math.floor(remainingAmount / p)
      const remainder = remainingAmount % p

      unlockedIndexes.forEach((idx, uIdx) => {
        let amt = base
        if (uIdx < Math.floor(remainder)) {
          amt = base + 1
        } else if (uIdx === Math.floor(remainder) && remainder % 1 > 0) {
          amt = base + (remainder % 1)
        }
        newSplits[idx].amount = amt > 0 ? amt : 0
      })
    }

    setSplits(newSplits)
  }

  const handleRemoveSplit = (index) => {
    if (splits.length > 2) {
      const originalAmount =
        selectedItem?.originalAmount || selectedItem?.amount || 0

      const newSplits = splits.filter((_, i) => i !== index)

      const newLockedIndexes = new Set()
      lockedIndexes.forEach((lockedIdx) => {
        if (lockedIdx < index) {
          newLockedIndexes.add(lockedIdx)
        } else if (lockedIdx > index) {
          newLockedIndexes.add(lockedIdx - 1)
        }
      })

      let totalLockedAmount = 0
      newSplits.forEach((split, idx) => {
        if (newLockedIndexes.has(idx)) {
          const amt =
            typeof split.amount === 'string'
              ? Number(split.amount?.replace(/\./g, '')?.replace(/\,/g, '.'))
              : split.amount
          totalLockedAmount += isNaN(amt) ? 0 : amt
        }
      })

      const remainingAmount = originalAmount - totalLockedAmount

      const unlockedIndexes = []
      newSplits.forEach((_, idx) => {
        if (!newLockedIndexes.has(idx)) {
          unlockedIndexes.push(idx)
        }
      })

      if (unlockedIndexes.length > 0) {
        const p = unlockedIndexes.length
        const base = Math.floor(remainingAmount / p)
        const remainder = remainingAmount % p

        unlockedIndexes.forEach((idx, uIdx) => {
          let amt = base
          if (uIdx < Math.floor(remainder)) {
            amt = base + 1
          } else if (uIdx === Math.floor(remainder) && remainder % 1 > 0) {
            amt = base + (remainder % 1)
          }
          newSplits[idx].amount = amt > 0 ? amt : 0
        })
      }

      setSplits(newSplits)
      setLockedIndexes(newLockedIndexes)
    }
  }

  const handleSplitChange = (index, field, value) => {
    const newSplits = [...splits]
    newSplits[index][field] = value

    if (field === 'amount' && !lockedIndexes.has(index)) {
      const originalAmount =
        selectedItem?.originalAmount || selectedItem?.amount || 0

      let totalAllocated = 0

      totalAllocated += isNaN(value) ? 0 : value

      newSplits.forEach((split, idx) => {
        if (lockedIndexes.has(idx) && idx !== index) {
          const amt = split.amount
          totalAllocated += isNaN(amt) ? 0 : amt
        }
      })

      const remaining = originalAmount - totalAllocated

      const unlockedIndexes = []
      newSplits.forEach((_, idx) => {
        if (!lockedIndexes.has(idx) && idx !== index) {
          unlockedIndexes.push(idx)
        }
      })

      if (unlockedIndexes.length > 0) {
        const p = unlockedIndexes.length
        const base = Math.floor(remaining / p)
        const remainder = remaining % p

        unlockedIndexes.forEach((idx, uIdx) => {
          let amt = base
          if (uIdx < Math.floor(remainder)) {
            amt = base + 1
          } else if (uIdx === Math.floor(remainder) && remainder % 1 > 0) {
            amt = base + (remainder % 1)
          }
          newSplits[idx].amount = amt > 0 ? amt : 0
        })
      }
    }

    setSplits(newSplits)
  }

  const getTotalSplitAmount = () => {
    return splits.reduce((total, split) => {
      const amount = split.amount
      return total + (isNaN(amount) ? 0 : amount)
    }, 0)
  }

  const handleSubmit = async () => {
    const hasInvalidAmount = splits.some((split) => {
      const amount = split.amount
      return isNaN(amount) || amount <= 0
    })

    if (hasInvalidAmount) {
      enqueueSnackbar('Semua bagian harus memiliki jumlah lebih dari 0', {
        variant: 'error',
      })
      return
    }

    setIsSubmitting(true)
    setIsSyncingSplitResult(false)

    try {
      const payloadArrNew = splits.map((split) => ({
        amount: truncateTwoDecimals(split.amount),
        itemId: selectedItem?.itemId,
        desc: '',
        unit: split.unit,
        ...((split?.bundle || selectedItem?.bundle) && {
          bundle: Number(split?.bundle || selectedItem?.bundle),
        }),
      }))

      if (remainingAmount > 0) {
        payloadArrNew.push({
          amount: truncateTwoDecimals(remainingAmount),
          itemId: selectedItem?.itemId,
          desc: '',
          unit: selectedItem?.unit?.value || selectedItem?.unit || 'Kg',
          ...(selectedItem?.bundle && {
            bundle: Number(selectedItem.bundle),
          }),
        })
      }

      const payload = {
        inventoryId: selectedItem?.inventoryId || selectedItem?.id,
        arrNew: payloadArrNew,
      }

      const splitResult = await dispatch(splitInventory(payload)).unwrap()
      setIsSyncingSplitResult(true)

      if (onSplitSuccess && splitResult && Array.isArray(splitResult)) {
        const returnCount =
          remainingAmount > 0 ? splits.length : splitResult.length
        await onSplitSuccess(splitResult.slice(0, returnCount))
      } else {
        await dispatch(getInventories())
      }

      enqueueSnackbar('Berhasil memisahkan inventory', { variant: 'success' })
      toggle()
    } catch (error) {
      enqueueSnackbar(error || 'Gagal memisahkan inventory', {
        variant: 'error',
      })
    } finally {
      setIsSyncingSplitResult(false)
      setIsSubmitting(false)
    }
  }

  const toggleLock = (index) => {
    const newLockedIndexes = new Set(lockedIndexes)
    if (newLockedIndexes.has(index)) {
      newLockedIndexes.delete(index)
    } else {
      newLockedIndexes.add(index)
    }
    setLockedIndexes(newLockedIndexes)
  }

  const originalAmount =
    selectedItem?.originalAmount || selectedItem?.amount || 0
  const totalSplit = getTotalSplitAmount()
  const remainingAmount =
    truncateTwoDecimals(originalAmount) - truncateTwoDecimals(totalSplit)
  const isBusy = isSubmitting || isSyncingSplitResult

  return (
    <Modal
      isOpen={isOpen}
      toggle={isBusy ? undefined : toggle}
      size="lg"
      style={{ maxWidth: '600px' }}
      centered
      keyboard={!isBusy}
      backdrop={isBusy ? 'static' : true}
      data-testid="sell-form-modal-split"
    >
      <ModalHeader toggle={isBusy ? undefined : toggle}>
        Pisah Inventori
      </ModalHeader>
      <ModalBody
        style={{
          overflowY: 'auto',
          minHeight: '300px',
          maxHeight: '60vh',
          padding: 14,
        }}
      >
        <div className="mb-4 p-3 bg-primary-50 border border-primary rounded">
          <h6 className="mb-3 text-primary">Informasi Item</h6>
          <Row>
            <Col md={6}>
              <div className="mb-2">
                <small className="text-muted">Nama Barang</small>
                <div className="fw-bold">{selectedItem?.nameItem || '-'}</div>
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-2">
                <small className="text-muted">Jumlah</small>
                <div className="fw-bold">
                  {intl.formatNumber(originalAmount, {
                    useGrouping: 'always',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2,
                  })}{' '}
                  {selectedItem?.unit?.value || selectedItem?.unit || ''}
                </div>
              </div>
            </Col>
          </Row>
          {(selectedItem?.lotCode || selectedItem?.ownerCode) && (
            <Row className="mt-2 pt-2 border-top">
              {selectedItem?.lotCode && (
                <Col md={6}>
                  <div className="mb-2">
                    <small className="text-muted">Lot ID</small>
                    <div className="fw-bold text-primary">
                      {selectedItem.lotCode}
                    </div>
                  </div>
                </Col>
              )}
              {/* {selectedItem?.ownerCode && (
                <Col md={6}>
                  <div className="mb-2">
                    <small className="text-muted">Kode Inventori</small>
                    <div className="fw-bold text-primary">
                      {selectedItem.ownerCode}
                    </div>
                  </div>
                </Col>
              )} */}
            </Row>
          )}
        </div>

        <h6 className="mb-3">Bagian-bagian Pisah</h6>
        {splits.map((split, index) => (
          <div key={index} className="mb-3 p-3 border rounded">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0">Bagian {index + 1}</h6>
              <div className="d-flex gap-2">
                <Button
                  color={lockedIndexes.has(index) ? 'warning' : 'secondary'}
                  size="sm"
                  outline
                  onClick={() => toggleLock(index)}
                  title={lockedIndexes.has(index) ? 'Unlock' : 'Lock'}
                >
                  {lockedIndexes.has(index) ? (
                    <Lock size={14} />
                  ) : (
                    <Unlock size={14} />
                  )}
                </Button>
                {splits.length > 2 && (
                  <Button
                    color="danger"
                    size="sm"
                    outline
                    onClick={() => handleRemoveSplit(index)}
                  >
                    <Trash size={14} />
                  </Button>
                )}
              </div>
            </div>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Nama Barang</Label>
                  <Input
                    type="text"
                    value={selectedItem?.nameItem || ''}
                    disabled
                    className="bg-light"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Jumlah</Label>
                  <PMBInputNumber
                    onFocus={(e) => e.target.select()}
                    name={`amount-${index}`}
                    value={
                      split.amount
                        ? newFormatThousandSeparator(split.amount)
                        : ''
                    }
                    onChange={(e) => {
                      const numericValue = parseFormattedNumber(e.target.value)
                      if (!isNaN(numericValue)) {
                        handleSplitChange(index, 'amount', numericValue)
                      }
                    }}
                    prefix={split.unit}
                    isLastPrefix
                    placeholder="0"
                    maxDecimalPlaces={3}
                  />
                </FormGroup>
              </Col>
            </Row>
          </div>
        ))}

        <div className="mt-3 p-3 bg-light rounded">
          <Row>
            <Col md={6} xs={12} className="mb-2 mb-md-0">
              <strong>Total bagian saat ini:</strong> {splits.length}
            </Col>
            <Col md={6} xs={12} className="text-md-end">
              <strong>Total jumlah:</strong>{' '}
              <span
                className={remainingAmount < 0 ? 'text-danger' : 'text-success'}
              >
                {intl.formatNumber(totalSplit, {
                  useGrouping: 'always',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 2,
                })}{' '}
                /{' '}
                {intl.formatNumber(originalAmount, {
                  useGrouping: 'always',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 2,
                })}{' '}
                {selectedItem?.unit?.value || selectedItem?.unit || ''}
              </span>
            </Col>
          </Row>
          {remainingAmount !== 0 && (
            <Row className="mt-2">
              <Col xs={12} className="text-md-end">
                <small
                  className={
                    remainingAmount < 0 ? 'text-danger' : 'text-warning'
                  }
                >
                  {remainingAmount < 0 ? 'Kelebihan' : 'Sisa'}:{' '}
                  {intl.formatNumber(Math.abs(remainingAmount), {
                    useGrouping: 'always',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2,
                  })}{' '}
                  {selectedItem?.unit?.value || selectedItem?.unit || ''}
                </small>
              </Col>
            </Row>
          )}
        </div>

        <div className="mt-3">
          <small className="text-muted">
            Jumlah bagian yang ingin ditambahkan
          </small>
          <div className="d-flex align-items-center gap-2 mt-2">
            <Button
              color="primary"
              outline
              size="sm"
              onClick={() => {
                if (addCount > 1) {
                  setAddCount(addCount - 1)
                }
              }}
              disabled={addCount <= 1}
            >
              <Minus size={14} />
            </Button>
            <Input
              onFocus={(e) => e.target.select()}
              type="number"
              min="0"
              value={addCount}
              onChange={(e) => {
                const val = parseInt(e.target.value)
                if (!isNaN(val) && val >= 1) {
                  setAddCount(val)
                }
              }}
              onKeyDown={(e) => {
                if (e.key === ',' || e.key === '.') {
                  e.preventDefault()
                }
              }}
              style={{ width: '80px', textAlign: 'center' }}
            />
            <Button
              color="primary"
              outline
              size="sm"
              onClick={() => setAddCount(addCount + 1)}
            >
              <Plus size={14} />
            </Button>
            <CustomButton
              color="primary"
              size="sm"
              onClick={() => {
                handleAddSplit(addCount)
                setAddCount(1)
              }}
              disabled={addCount <= 0 || isBusy}
            >
              Tambah Bagian
            </CustomButton>
          </div>
        </div>

        {isSyncingSplitResult && (
          <div className="mt-3 p-3 bg-light rounded border text-center">
            <small className="text-muted">
              Sedang menyinkronkan hasil split inventori. Mohon tunggu...
            </small>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          className="btn btn-cancel"
          onClick={toggle}
          disabled={isBusy}
          data-testid="sell-form-modal-split-close"
        >
          Batal
        </Button>
        <CustomButton
          color="primary"
          onClick={handleSubmit}
          disabled={isBusy || remainingAmount < 0}
          testId="sell-form-modal-split-submit"
        >
          {isSyncingSplitResult
            ? 'Menyinkronkan...'
            : isSubmitting
              ? 'Memproses...'
              : 'Pisah'}
        </CustomButton>
      </ModalFooter>
    </Modal>
  )
}

export default ModalSplit
